/*
SPDX-FileCopyrightText: 2024 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { useEffect } from 'react';
import { defineComponent } from '../board/Utils';
import { deepCopy, isEmptyObject } from '../general/Utils';
export function getComponentsAbove(id, list) {
    var index = list.indexOf(id);
    return list.slice(0, index + 1);
}
export function getComponentsBelow(id, list, indexOffset) {
    if (indexOffset === void 0) { indexOffset = 0; }
    var index = list.indexOf(id);
    return index === -1 ? [] : list.slice(index + indexOffset + 1); // adding 1, as by default we want the next component
}
export function filterHasUpdated(setFilter, exisitingFilter, incomingFilter) {
    // force setFilter update if zone is not defined - this only occurs on initial load
    if (incomingFilter === undefined) {
        setFilter(undefined);
        return false;
    }
    var exisiting = JSON.stringify(exisitingFilter);
    var incoming = JSON.stringify(incomingFilter);
    var hasUpdated = exisiting !== incoming;
    if (hasUpdated)
        setFilter(deepCopy(incomingFilter));
    return hasUpdated;
}
export function mergeAndFilters(target, incoming) {
    var output = deepCopy(target);
    for (var id in incoming) {
        var currentOut = id in output ? output[id] : {};
        var currentIn = id in incoming ? incoming[id] : {};
        output[id] = Object.assign(currentOut, currentIn);
    }
    return output;
}
export function generateFilter(zone, id, includeSubFilter) {
    if (zone === undefined)
        return undefined;
    var z = zone;
    var aboveComponents = id ? getComponentsAbove(id, z.order) : z.order;
    var compoundedFilter = z.filter ? z.filter.and_ : {};
    // loop through above components
    for (var _i = 0, aboveComponents_1 = aboveComponents; _i < aboveComponents_1.length; _i++) {
        var currentId = aboveComponents_1[_i];
        // exclude pass throughs except self
        if (z.components[currentId].data.filterPassThrough && id !== currentId) {
            continue;
        }
        var currentFilter = z.components[currentId].data.filter.and_;
        // include sub filter if required
        var subFilter = z.components[currentId].data.subFilter;
        if ((currentId !== id || includeSubFilter) && subFilter) {
            currentFilter = mergeAndFilters(currentFilter, subFilter.and_);
        }
        compoundedFilter = mergeAndFilters(currentFilter, compoundedFilter);
    }
    return {
        and_: compoundedFilter
    };
}
// insert a value in a list after an id located
export function addValueBelow(id, value, list) {
    var idIndex = list.indexOf(id);
    list.splice(idIndex + 1, 0, value);
    return list;
}
export function addComponentBelow(id, newId, zone) {
    defineComponent({
        id: newId,
        filterPassThrough: zone.components[id].data.filterPassThrough
    }, zone);
    zone.order = addValueBelow(id, newId, zone.order);
}
export function resetFiltersBelow(params) {
    var zone = params.zone, indexOffset = params.indexOffset;
    var id = params.id;
    var z = zone;
    for (var _i = 0, _a = getComponentsBelow(id, z.order, indexOffset); _i < _a.length; _i++) {
        var currentId = _a[_i];
        z.components[currentId].data.filter = deepCopy(z.components[currentId].data.defaultFilter);
        z.components[currentId].data.subFilter = undefined;
    }
}
export function resetAllFilters(zone) {
    zone.filter = deepCopy(zone.defaultFilter);
    for (var _i = 0, _a = zone.order; _i < _a.length; _i++) {
        var currentId = _a[_i];
        zone.components[currentId].data.filter = deepCopy(zone.components[currentId].data.defaultFilter);
        zone.components[currentId].data.subFilter = undefined;
    }
}
export function removeComponent(id, zone) {
    delete zone.components[id];
    zone.order = zone.order.filter(function (currentId) { return currentId !== id; });
}
export function setFilter(params) {
    var _a;
    var operator = params.operator, value = params.value, negate = params.negate, exists = params.exists, attribute = params.attribute, componentId = params.componentId, zone = params.zone, valueExists = params.valueExists;
    var z = zone;
    var and_ = z.components[componentId].data.filter.and_;
    resetFiltersBelow({ id: componentId, zone: z });
    if (valueExists || exists) {
        // exists filter removed if value is already set
        if ('exists' in (and_[attribute] || {})) {
            delete and_[attribute]['exists'];
        }
        // setting just an exists filter if exists is true
        if (exists) {
            and_[attribute] = {};
            and_[attribute]['exists'] = { negate: negate };
            // setting a value filter from an input
        }
        else if (operator === 'in_list') {
            and_[attribute] = {};
            and_[attribute]['in_list'] = { value: value, negate: negate };
        }
        else {
            and_[attribute] = __assign(__assign({}, and_[attribute]), (_a = {}, _a[operator] = { value: value, negate: negate }, _a));
        }
    }
    else {
        if (operator in (and_[attribute] || {})) {
            delete and_[attribute][operator];
        }
    }
    if (attribute in and_ && isEmptyObject(and_[attribute])) {
        delete and_[attribute];
    }
}
export function filterListener(params, dependencies) {
    var attribute = params.attribute, componentId = params.componentId, operators = params.operators, zone = params.zone, setValue = params.setValue, setDisabled = params.setDisabled, setNegate = params.setNegate, setExists = params.setExists, zoneToValue = params.zoneToValue;
    useEffect(function () {
        var aboveComponents = getComponentsAbove(componentId, zone.order);
        var disabled = false;
        var readyToBreak = 0;
        var value = params.emptyValue;
        var negate = false;
        var exists = false;
        for (var _i = 0, aboveComponents_2 = aboveComponents; _i < aboveComponents_2.length; _i++) {
            var currentId = aboveComponents_2[_i];
            var componentData = zone.components[currentId].data;
            var and_ = componentData.filter.and_;
            // ignore pass throughs
            if (and_ && attribute in and_ && !componentData.filterPassThrough) {
                var disableCondition = (currentId !== componentId);
                // checks setExists as only used for text input filters
                if (setExists && 'exists' in and_[attribute]) {
                    exists = true;
                    disabled = disableCondition;
                    negate = and_[attribute]['exists'].negate || negate;
                    break;
                }
                else {
                    for (var _a = 0, operators_1 = operators; _a < operators_1.length; _a++) {
                        var op = operators_1[_a];
                        if (op in and_[attribute]) {
                            var filter = and_[attribute][op];
                            disabled = disableCondition;
                            negate = filter.negate || negate;
                            value = zoneToValue(filter.value, value);
                            // break if all operators have been checked
                            if (readyToBreak === operators.length - 1)
                                break;
                            readyToBreak++;
                        }
                    }
                }
            }
        }
        setValue(value);
        setDisabled(disabled);
        if (setExists)
            setExists(exists);
        if (setNegate)
            setNegate(negate);
    }, dependencies);
}
export function addSubFilter(params) {
    var id = params.id, filter = params.filter, zone = params.zone;
    var z = zone;
    var f = filter;
    resetFiltersBelow({ id: id, zone: z });
    z.components[id].data.subFilter = f;
}
export function resetZone(params) {
    var zone = params.zone, setZone = params.setZone;
    resetAllFilters(zone);
    setZone(__assign({}, zone));
}
